import React, { useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaceHeatmap } from './FaceHeatmap';
import type { PremiumJobCondition } from '../types/api';
import { premiumConditionLabel } from '../utils/conditionLabels';
import { useReportExport } from '../reports/useReportExport';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  imagePath: string;
  analysis: PremiumJobCondition[];
  jobId?: string;
}

const isNone = (val: string | undefined | null) => {
  if (!val) return true;
  const lower = val.toLowerCase();
  return lower === 'none' || lower === 'not detected' || lower === 'unknown';
};

function getConditionIcon(name: string) {
  const n = name.toLowerCase();
  if (n.includes('acne') || n.includes('blemish')) return 'vignette';
  if (n.includes('wrinkle') || n.includes('line')) return 'waves';
  if (n.includes('pigment') || n.includes('spot') || n.includes('sun')) return 'filter_tilt_shift';
  if (n.includes('circle') || n.includes('eye')) return 'visibility';
  if (n.includes('hydrat') || n.includes('dry') || n.includes('oil')) return 'water_drop';
  if (n.includes('redness')) return 'blur_on';
  if (n.includes('texture') || n.includes('rough') || n.includes('pore')) return 'drag_indicator';
  return 'face_retouching_natural';
}

export function DetailedFaceAnalysisModal({ isOpen, onClose, imagePath, analysis, jobId }: Props) {
  const { exporting, exportReport, errorToast } = useReportExport();
    const [zoom, setZoom] = useState(1);
    const [activeFilters, setActiveFilters] = useState<string[]>([]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    const filteredAnalysis = useMemo(
        () => analysis.filter(c => !isNone(c.condition_name) && !isNone(c.severity)),
        [analysis]
    );

    useEffect(() => {
        if (isOpen && activeFilters.length === 0 && filteredAnalysis.length > 0) {
            setActiveFilters(Array.from(new Set(filteredAnalysis.map(c => c.condition_name))));
        }
    }, [isOpen, filteredAnalysis, activeFilters.length]);

    const toggleFilter = (name: string) => {
        setActiveFilters(prev =>
            prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
        );
    };

    /* Condition colors are semantic domain colors, not brand palette — kept intentionally distinct */
    const CONDITION_COLORS: Record<string, { hex: string; label: string }> = {
        acne:       { hex: '#be123c', label: 'red' },
        wrinkle:    { hex: '#4338ca', label: 'indigo' },
        pigment:    { hex: '#c2410c', label: 'orange' },
        eye:        { hex: '#7e22ce', label: 'purple' },
        hydration:  { hex: '#0369a1', label: 'sky' },
        redness:    { hex: '#be185d', label: 'pink' },
        texture:    { hex: '#a16207', label: 'amber' },
        default:    { hex: '#1A6F70', label: 'teal' },
    };

    function getConditionColor(name: string) {
        const n = name.toLowerCase();
        if (n.includes('acne') || n.includes('blemish') || n.includes('pimple')) return CONDITION_COLORS.acne;
        if (n.includes('wrinkle') || n.includes('line') || n.includes('aging')) return CONDITION_COLORS.wrinkle;
        if (n.includes('pigment') || n.includes('spot') || n.includes('sun')) return CONDITION_COLORS.pigment;
        if (n.includes('circle') || n.includes('eye')) return CONDITION_COLORS.eye;
        if (n.includes('hydrat') || n.includes('dry') || n.includes('oil')) return CONDITION_COLORS.hydration;
        if (n.includes('redness')) return CONDITION_COLORS.redness;
        if (n.includes('texture') || n.includes('rough') || n.includes('pore')) return CONDITION_COLORS.texture;
        return CONDITION_COLORS.default;
    }

    const EASE: [number, number, number, number] = [0.22, 0.61, 0.36, 1];

    const modalContent = (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    key="backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.28, ease: EASE }}
                    className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
                    style={{ background: 'rgba(31,36,33,0.82)', backdropFilter: 'blur(16px) saturate(1.2)' }}
                    onClick={onClose}
                >
                    <motion.div
                        key="panel"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 32 }}
                        transition={{ duration: 0.44, ease: EASE }}
                        className="w-full max-w-7xl h-[96vh] sm:h-full sm:max-h-[95vh] overflow-hidden flex flex-col lg:flex-row relative"
                        style={{
                            background: 'var(--c-charcoal)',
                            borderRadius: '32px 32px 0 0',
                            border: '1px solid rgba(248,244,238,0.08)',
                            boxShadow: 'var(--sh-deep)',
                        }}
                        onClick={e => { e.stopPropagation(); }}
                    >
                        {/* Close — floats above both panels */}
                        <button
                            onClick={onClose}
                            aria-label="Close"
                            className="btn--hud absolute top-4 right-4 z-[100] w-9 h-9 flex items-center justify-center rounded-full"
                        >
                            <span className="material-symbols-outlined text-[18px]">close</span>
                        </button>

                        {/* Mobile header */}
                        <div
                            className="lg:hidden flex items-center gap-3 px-6 py-4 border-b z-10"
                            style={{ borderColor: 'rgba(248,244,238,0.08)', background: 'rgba(31,36,33,0.95)' }}
                        >
                            {/* Drag pill */}
                            <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full" style={{ background: 'rgba(248,244,238,0.2)' }} />
                            <div
                                className="w-8 h-8 rounded-[14px] flex items-center justify-center"
                                style={{ background: 'rgba(42,164,166,0.18)' }}
                            >
                                <span className="material-symbols-outlined text-[18px]" style={{ color: 'var(--c-kivo-teal)' }}>face_retouching_natural</span>
                            </div>
                            <h2 className="font-display font-medium text-[18px] leading-[1.2]" style={{ color: 'var(--c-cream)', letterSpacing: '-0.01em' }}>
                                Analysis Map
                            </h2>
                        </div>

                        {/* ── Image panel ─────────────────────────────────── */}
                        <div className="flex-1 relative overflow-hidden flex flex-col" style={{ background: 'var(--c-charcoal)' }}>

                            {/* Desktop header */}
                            <div
                                className="hidden lg:flex items-center justify-between px-8 py-5 border-b z-10"
                                style={{
                                    borderColor: 'rgba(248,244,238,0.06)',
                                    background: 'rgba(31,36,33,0.6)',
                                    backdropFilter: 'blur(12px)',
                                }}
                            >
                                <div className="flex items-center gap-3">
                                    <div
                                        className="w-9 h-9 rounded-[14px] flex items-center justify-center"
                                        style={{ background: 'rgba(42,164,166,0.16)' }}
                                    >
                                        <span className="material-symbols-outlined text-[20px]" style={{ color: 'var(--c-kivo-teal)' }}>face_retouching_natural</span>
                                    </div>
                                    <div>
                                        <h2 className="font-display font-medium text-[20px] leading-[1.1]" style={{ color: 'var(--c-cream)', letterSpacing: '-0.015em' }}>
                                            Precision Analysis Map
                                        </h2>
                                        <p className="font-text text-[10px] uppercase mt-0.5" style={{ color: 'rgba(248,244,238,0.35)', letterSpacing: 'var(--tracking-label)' }}>
                                            Optical Engine · Active Heatmap
                                        </p>
                                    </div>
                                </div>

                                {/* Live badge */}
                                <div
                                    className="flex items-center gap-2 font-text text-[10px] uppercase tracking-[0.15em] px-4 py-2 rounded-full"
                                    style={{
                                        color: 'var(--c-kivo-teal)',
                                        background: 'var(--c-teal-weak)',
                                        border: '1px solid rgba(42,164,166,0.20)',
                                        letterSpacing: '0.15em',
                                    }}
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                    Live Status: Active
                                </div>
                            </div>

                            {/* Scan-wave texture overlay */}
                            <div
                                className="absolute inset-0 pointer-events-none opacity-[0.06] z-0"
                                style={{
                                    backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='32' viewBox='0 0 240 32'><path d='M0 16 C 30 4, 60 4, 90 16 S 150 28, 180 16 210 4, 240 16' fill='none' stroke='%23F8F4EE' stroke-opacity='0.4' stroke-width='1.1' stroke-linecap='round'/></svg>")`,
                                    backgroundRepeat: 'repeat',
                                    backgroundSize: '240px 32px',
                                }}
                            />

                            {/* Image area */}
                            <div className="flex-1 flex items-center justify-center p-4 sm:p-8 relative overflow-hidden z-10">

                                {/* Zoom controls */}
                                <div className="absolute top-6 right-6 z-20 flex flex-col gap-2.5">
                                    {[{ icon: 'add', action: () => { setZoom(z => Math.min(3, z + 0.2)); }, label: 'Zoom in' },
                                      { icon: 'remove', action: () => { setZoom(z => Math.max(0.5, z - 0.2)); }, label: 'Zoom out' }]
                                    .map(({ icon, action, label }) => (
                                        <button
                                            key={icon}
                                            onClick={action}
                                            aria-label={label}
                                            className="btn--hud w-12 h-12 flex items-center justify-center rounded-full"
                                        >
                                            <span className="material-symbols-outlined text-[22px]">{icon}</span>
                                        </button>
                                    ))}
                                </div>

                                {/* Zoom level */}
                                <div
                                    className="absolute bottom-6 right-6 z-20 flex items-center gap-2.5 px-5 py-2.5 rounded-[14px]"
                                    style={{
                                        background: 'rgba(31,36,33,0.55)',
                                        border: '1px solid rgba(248,244,238,0.08)',
                                        backdropFilter: 'blur(12px)',
                                    }}
                                >
                                    <span className="material-symbols-outlined text-[18px]" style={{ color: 'var(--c-kivo-teal)' }}>zoom_in</span>
                                    <span className="font-text text-[11px] uppercase tracking-[0.15em]" style={{ color: 'var(--c-cream)' }}>
                                        {Math.round(zoom * 100)}%
                                    </span>
                                </div>

                                {/* Legend */}
                                <div
                                    className="absolute bottom-5 left-5 z-20 p-4 max-w-[calc(100%-6rem)] hidden sm:block rounded-[14px]"
                                    style={{
                                        background: 'rgba(248,244,238,0.92)',
                                        border: '1px solid var(--c-charcoal-06)',
                                        backdropFilter: 'blur(12px)',
                                        boxShadow: 'var(--sh-soft)',
                                    }}
                                >
                                    <h4
                                        className="t-label mb-3"
                                    >
                                        Active Layers
                                    </h4>
                                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                                        {filteredAnalysis.slice(0, 6).map((c, idx) => {
                                            const color = getConditionColor(c.condition_name);
                                            return (
                                                <div key={idx} className="flex items-center gap-1.5">
                                                    <span
                                                        className="w-2 h-2 rounded-full shrink-0"
                                                        style={{ backgroundColor: color.hex }}
                                                    />
                                                    <span className="font-text text-[12px] font-medium capitalize text-[var(--ink)]">
                                                        {premiumConditionLabel(c.condition_name)}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                        {filteredAnalysis.length > 6 && (
                                            <span className="font-text text-[10px]" style={{ color: 'var(--c-charcoal-30)' }}>
                                                +{filteredAnalysis.length - 6} more
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Heatmap image. Fill the (definite, flex-1)
                                    panel so FaceHeatmap's object-contain image
                                    resolves against a real height and shows in
                                    full — otherwise it renders at natural size
                                    and the panel's overflow clips it even at
                                    lower zoom levels. */}
                                <motion.div
                                    className="relative w-full h-full flex items-center justify-center overflow-hidden rounded-[14px]"
                                    style={{
                                        boxShadow: 'var(--sh-deep)',
                                    }}
                                    animate={{ scale: zoom }}
                                    transition={{ type: 'spring', stiffness: 200, damping: 22 }}
                                >
                                    <FaceHeatmap
                                        imagePath={imagePath}
                                        analysis={filteredAnalysis}
                                        activeConditions={activeFilters}
                                        scale={1}
                                        enableBreathing={false}
                                    />
                                    {/* Subtle grid overlay */}
                                    <div
                                        className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-20"
                                        style={{
                                            background: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
                                            backgroundSize: '40px 40px',
                                        }}
                                    />
                                </motion.div>
                            </div>
                        </div>

                        {/* ── Sidebar ─────────────────────────────────────── */}
                        <div
                            className="w-full lg:w-[400px] shrink-0 flex flex-col z-10 overflow-y-auto max-h-[48vh] lg:max-h-full"
                            style={{
                                background: 'var(--c-cream)',
                                borderTop: '1px solid var(--c-charcoal-06)',
                            }}
                        >
                            <div className="p-7 lg:p-8">
                                {/* Section label */}
                                <h3
                                    className="t-label mb-6 flex items-center gap-2"
                                >
                                    <span className="material-symbols-outlined text-[16px]" style={{ color: 'rgba(26,111,112,0.5)' }}>layers</span>
                                    Structural Layers
                                </h3>

                                {/* Condition cards */}
                                <motion.div
                                    className="space-y-2.5"
                                    initial="hidden"
                                    animate="visible"
                                    variants={{ visible: { transition: { staggerChildren: 0.045 } } }}
                                >
                                    {filteredAnalysis.map((c, idx) => {
                                        const isActive = activeFilters.includes(c.condition_name);
                                        const color = getConditionColor(c.condition_name);
                                        const icon = getConditionIcon(c.condition_name);

                                        return (
                                            <motion.div
                                                key={idx}
                                                variants={{
                                                    hidden: { opacity: 0, x: 12 },
                                                    visible: { opacity: 1, x: 0, transition: { duration: 0.32, ease: EASE } },
                                                }}
                                                className="group relative flex items-center justify-between p-4 rounded-[22px] cursor-pointer transition-all duration-[320ms]"
                                                style={{
                                                    background: isActive ? 'white' : 'var(--c-sand)',
                                                    border: isActive ? '1px solid var(--c-charcoal-06)' : '1px solid transparent',
                                                    boxShadow: isActive ? 'var(--sh-soft)' : 'none',
                                                    opacity: isActive ? 1 : 0.68,
                                                }}
                                                onClick={() => { toggleFilter(c.condition_name); }}
                                                whileHover={{ x: -3, opacity: 1 }}
                                                whileTap={{ scale: 0.98 }}
                                            >
                                                <div className="flex items-center gap-3.5 flex-1 min-w-0">
                                                    {/* Icon */}
                                                    <div
                                                        className="w-11 h-11 rounded-[14px] flex items-center justify-center text-white shrink-0 transition-transform duration-[320ms] group-hover:scale-105"
                                                        style={{
                                                            backgroundColor: color.hex,
                                                            boxShadow: `0 4px 12px ${color.hex}40`,
                                                        }}
                                                    >
                                                        <span className="material-symbols-outlined text-[20px]">{icon}</span>
                                                    </div>

                                                    {/* Text */}
                                                    <div className="flex-1 min-w-0">
                                                        <span
                                                            className="font-display font-medium text-[14px] leading-[1.2] block capitalize truncate mb-1"
                                                            style={{ color: 'var(--ink)', letterSpacing: '-0.005em' }}
                                                        >
                                                            {premiumConditionLabel(c.condition_name)}
                                                        </span>
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex gap-0.5">
                                                                {[1, 2, 3].map(dot => (
                                                                    <div
                                                                        key={dot}
                                                                        className="w-1.5 h-1 rounded-full transition-colors"
                                                                        style={{
                                                                            background: dot <= (c.locations.length > 2 ? 3 : c.locations.length)
                                                                                ? 'var(--c-kivo-teal)'
                                                                                : 'rgba(31,36,33,0.15)',
                                                                        }}
                                                                    />
                                                                ))}
                                                            </div>
                                                            <span
                                                                className="font-text text-[10px] uppercase tracking-[0.15em]"
                                                                style={{ color: 'var(--c-charcoal-30)' }}
                                                            >
                                                                {c.locations.length} points
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Toggle */}
                                                <div
                                                    className="w-10 h-[22px] rounded-full relative shrink-0 ml-2 transition-colors duration-[320ms]"
                                                    style={{ background: isActive ? 'var(--c-kivo-teal)' : 'var(--c-charcoal-12)' }}
                                                >
                                                    <motion.div
                                                        layout
                                                        className="w-[14px] h-[14px] bg-white rounded-full absolute top-1 left-1 shadow-sm"
                                                        animate={{ x: isActive ? 18 : 0 }}
                                                        transition={{ type: 'spring', stiffness: 500, damping: 32 }}
                                                    />
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </motion.div>

                                {/* Insight panel */}
                                <div className="mt-7 pt-7" style={{ borderTop: '1px solid var(--c-charcoal-06)' }}>
                                    <h3
                                        className="t-label mb-4 flex items-center gap-2"
                                    >
                                        <span className="material-symbols-outlined text-[16px]" style={{ color: 'rgba(26,111,112,0.5)' }}>insights</span>
                                        Skincare Observation
                                    </h3>

                                    <div
                                        className="relative overflow-hidden rounded-[22px] p-5"
                                        style={{
                                            background: 'var(--c-sand)',
                                            border: '1px solid var(--c-charcoal-06)',
                                        }}
                                    >
                                        {/* Ghost icon */}
                                        <div className="absolute top-3 right-3 pointer-events-none" style={{ opacity: 0.06 }}>
                                            <span className="material-symbols-outlined text-[52px]" style={{ color: 'var(--c-deep-teal)' }}>psychology</span>
                                        </div>

                                        <p
                                            className="font-display font-medium text-[15px] leading-[1.3] mb-2 relative z-10"
                                            style={{ color: 'var(--ink)', letterSpacing: '-0.005em' }}
                                        >
                                            Precision Surface Analysis
                                        </p>
                                        <p
                                            className="font-text text-[13px] leading-[1.55] relative z-10"
                                            style={{ color: 'rgba(31,36,33,0.58)' }}
                                        >
                                            Our sensors identified {filteredAnalysis.length} structural characteristic{filteredAnalysis.length !== 1 ? 's' : ''}.
                                            Explore each layer to understand your skin&apos;s unique landscape.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Action area */}
                            <div
                                className="mt-auto p-7 lg:p-8"
                                style={{
                                    borderTop: '1px solid var(--c-charcoal-06)',
                                    background: 'var(--c-sand)',
                                }}
                            >
                                <motion.button
                                    whileHover={{ y: -1 }}
                                    whileTap={{ scale: 0.97 }}
                                    onClick={() => { if (jobId) void exportReport('premium', jobId); }}
                                    disabled={exporting || !jobId}
                                    className="btn btn--primary w-full font-text font-medium text-[13px] uppercase py-3.5 px-6 rounded-full flex items-center justify-center gap-2.5"
                                    style={{
                                        boxShadow: '0 4px 12px rgba(26,111,112,0.20), 0 16px 40px rgba(26,111,112,0.14)',
                                        letterSpacing: '0.15em',
                                    }}
                                >
                                    <span className="material-symbols-outlined text-[18px]">download</span>
                                    Generate Map Report
                                </motion.button>
                                {errorToast}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    if (typeof document === 'undefined') return null;
    return ReactDOM.createPortal(modalContent, document.body);
}
